/**
 * 红色商务模板 - VIP会员页
 * 
 * 设计理念：沉浸式全屏 Hero + 横向套餐对比 + 权益展示
 * - 顶部：红色渐变全宽 Hero 区，王冠图标 + 几何装饰
 * - 中段：VIP 权益横向展示（非网格，强调品牌感）
 * - 套餐：三栏对比式布局，推荐位高亮突出
 * - 支付：内联支付方式选择 + CTA
 * - 与共享VIP（橙色居中卡片式）完全区分
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import RedNavbar from "@/templates/red/components/Navbar";
import RedFooter from "@/templates/red/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getVipPlans, getSiteSettings } from "@/utils/adminApi";

// ==================== API 层 ====================

interface PaymentConfig {
  alipay_enabled: boolean;
  wechat_enabled: boolean;
  qrcode_enabled: boolean;
  qrcode_priority: boolean;
  alipay_qrcode: string;
  wechat_qrcode: string;
}

async function fetchPaymentConfig(): Promise<PaymentConfig | null> {
  try {
    const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
    const siteId = localStorage.getItem('current_site_id') || '';
    const res = await fetch("/api/admin/settings/payment", {
      method: "GET",
      headers: {
        "Authorization": token ? `Bearer ${token}` : "",
        'X-Site-Id': siteId,
      },
    });
    const data = await res.json();
    if (data.code === 0 && data.data) {
      return {
        alipay_enabled: !!data.data.alipay_enabled,
        wechat_enabled: !!data.data.wechat_enabled,
        qrcode_enabled: !!data.data.qrcode_enabled,
        qrcode_priority: !!data.data.qrcode_priority,
        alipay_qrcode: data.data.alipay_qrcode || "",
        wechat_qrcode: data.data.wechat_qrcode || "",
      };
    }
    return null;
  } catch (e) {
    console.error("❌ VIP: 读取支付配置失败", e);
    return null;
  }
}

// ==================== 类型 ====================

interface VipPlan {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  duration: string;
  badge: string;
  features: string[];
  duration_months: number;
}

const DEFAULT_VIP_PLANS: VipPlan[] = [
  {
    id: "monthly",
    name: "月度会员",
    price: 29,
    originalPrice: 49,
    duration: "1个月",
    badge: "",
    features: [
      "信息置顶展示 3次/月",
      "发布信息不限条数",
      "专属会员标识",
      "优先客服支持",
    ],
    duration_months: 1,
  },
  {
    id: "quarterly",
    name: "季度会员",
    price: 79,
    originalPrice: 147,
    duration: "3个月",
    badge: "热门",
    features: [
      "信息置顶展示 10次/季",
      "发布信息不限条数",
      "专属会员标识",
      "优先客服支持",
      "数据统计分析",
    ],
    duration_months: 3,
  },
  {
    id: "yearly",
    name: "年度会员",
    price: 199,
    originalPrice: 588,
    duration: "12个月",
    badge: "超值",
    features: [
      "信息置顶展示 不限次数",
      "发布信息不限条数",
      "专属会员标识",
      "专属客服支持",
      "数据统计分析",
      "优先审核通道",
      "品牌推广曝光",
    ],
    duration_months: 12,
  },
];

const VIP_BENEFITS = [
  { icon: "ri-rocket-line", title: "置顶曝光", desc: "信息优先展示在列表顶部，获得更多浏览量", color: "from-red-500 to-rose-500" },
  { icon: "ri-shield-check-line", title: "认证标识", desc: "专属VIP认证标识，提升企业可信度", color: "from-red-600 to-red-500" },
  { icon: "ri-bar-chart-line", title: "数据分析", desc: "查看信息浏览、联系等详细数据统计", color: "from-rose-500 to-pink-500" },
  { icon: "ri-customer-service-line", title: "专属客服", desc: "优先响应，专属客服一对一服务", color: "from-red-500 to-orange-500" },
  { icon: "ri-infinity-line", title: "无限发布", desc: "不限条数发布服务信息，无需担心限制", color: "from-red-600 to-rose-600" },
  { icon: "ri-speed-line", title: "极速审核", desc: "信息优先审核，快速上线展示", color: "from-rose-600 to-red-500" },
];

// ==================== 页面组件 ====================

export default function RedVipPage() {
  const { isLoggedIn } = useAuth();
  const { siteConfig } = useSiteConfig();
  const [vipPlans, setVipPlans] = useState<VipPlan[]>(DEFAULT_VIP_PLANS);
  const [selectedPlan, setSelectedPlan] = useState("quarterly");
  const [payMethod, setPayMethod] = useState<"wechat" | "alipay" | "qrcode">("wechat");
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [enableVip, setEnableVip] = useState(true);

  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";

  // 加载数据
  useEffect(() => {
    async function loadData() {
      try {
        const [plans, settings, payments] = await Promise.all([
          getVipPlans().catch(() => null),
          getSiteSettings().catch(() => null),
          fetchPaymentConfig(),
        ]);

        if (settings) {
          setEnableVip(settings.enable_vip !== false);
        }

        if (plans && Array.isArray(plans) && plans.length > 0) {
          const formattedPlans = plans.map((p: any) => ({
            id: p.id,
            name: p.name || "VIP套餐",
            price: p.amount || p.price || 0,
            originalPrice: p.original_price || p.originalPrice || 0,
            duration: `${p.duration_months || p.duration || 1}个月`,
            badge: p.is_recommended ? "推荐" : "",
            features: p.benefits || p.features || [],
            duration_months: p.duration_months || 1,
          }));
          setVipPlans(formattedPlans);
          if (formattedPlans.length > 0) {
            setSelectedPlan(formattedPlans[0].id);
          }
        }

        if (payments) {
          setPaymentConfig(payments);
          if (payments.qrcode_enabled && payments.qrcode_priority) {
            setPayMethod("qrcode");
          }
        }
      } catch (e) {
        console.error("加载VIP数据失败:", e);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const currentPlan = vipPlans.find((p) => p.id === selectedPlan);

  const showAlipay = !!paymentConfig?.alipay_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.alipay_enabled);
  const showWechat = !!paymentConfig?.wechat_qrcode && (!!paymentConfig?.qrcode_enabled || !!paymentConfig?.wechat_enabled);
  const showQrcode = showAlipay && showWechat;

  useEffect(() => {
    if (payMethod === "wechat" && !showWechat) {
      setPayMethod(showAlipay ? "alipay" : "qrcode");
    } else if (payMethod === "alipay" && !showAlipay) {
      setPayMethod(showWechat ? "wechat" : "qrcode");
    } else if (payMethod === "qrcode" && !showQrcode) {
      setPayMethod(showWechat ? "wechat" : "alipay");
    }
  }, [showWechat, showAlipay, showQrcode]);

  const handlePay = useCallback(() => {
    if (!isLoggedIn) {
      window.location.href = "/login?redirect=/vip";
      return;
    }
    setShowPayModal(true);
  }, [isLoggedIn]);

  return (
    <div className="min-h-screen bg-white">
      <RedNavbar />

      <main className="pt-14">
        {/* VIP功能已关闭提示 */}
        {!enableVip && !loading && (
          <div className="w-full px-4 py-24">
            <div className="max-w-md mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-gray-100">
                <i className="ri-vip-crown-line text-4xl text-gray-400" />
              </div>
              <h1 className="text-xl font-bold text-gray-800 mb-3">VIP会员功能已关闭</h1>
              <p className="text-gray-500 mb-6">非常抱歉，VIP会员功能目前暂时关闭，如有需要请联系管理员。</p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors">
                <i className="ri-arrow-left-line" />
                返回首页
              </Link>
            </div>
          </div>
        )}

        {/* ── 主体内容 ── */}
        {enableVip && (
          <>
            {/* ── Hero 区：全宽红色渐变 ── */}
            <section className="relative overflow-hidden bg-gradient-to-br from-red-600 via-red-700 to-rose-800">
              {/* 装饰几何图形 */}
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full -translate-y-1/4 translate-x-1/4" />
              <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-red-400/20 rounded-full blur-3xl" />
              <div className="absolute top-10 left-0 w-40 h-40 bg-rose-300/10 rounded-full blur-2xl" />
              
              {/* 网格纹理 */}
              <div className="absolute inset-0 opacity-[0.03]" style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }} />

              <div className="relative z-10 w-full px-4 md:px-8 py-16 md:py-20">
                <div className="max-w-4xl mx-auto text-center">
                  {/* 徽标 */}
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-sm text-white/90 mb-6 border border-white/10">
                    <i className="ri-vip-crown-line text-amber-400" />
                    <span>VIP 会员</span>
                  </div>

                  <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
                    解锁<span className="text-amber-300">会员权益</span>
                    <br className="md:hidden" />
                    让您的服务脱颖而出
                  </h1>
                  <p className="text-red-100/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                    置顶曝光 · 无限发布 · 专属认证 · 数据分析 · 极速审核
                  </p>

                  {/* 装饰分割线 */}
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <div className="h-px w-16 bg-gradient-to-r from-transparent via-white/30 to-white/50" />
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <div className="h-px w-16 bg-gradient-to-l from-transparent via-white/30 to-white/50" />
                  </div>
                </div>
              </div>
            </section>

            {/* ── 权益展示区：横向卡片带渐变图标 ── */}
            <section className="relative z-10 -mt-10 w-full px-4 md:px-8 pb-10">
              <div className="max-w-5xl mx-auto">
                <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-8">
                    VIP <span className="text-red-600">专属权益</span>
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                    {VIP_BENEFITS.map((b) => (
                      <div key={b.title} className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-5 hover:shadow-md hover:border-red-100 transition-all duration-300">
                        {/* 顶部彩色渐变条 */}
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${b.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                        <div className={`w-11 h-11 mb-3 flex items-center justify-center rounded-xl bg-gradient-to-br ${b.color} text-white shadow-lg`}>
                          <i className={`${b.icon} text-xl`} />
                        </div>
                        <h3 className="font-semibold text-gray-800 mb-1 text-sm">{b.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* ── 套餐选择区：横向对比布局 ── */}
            <section className="w-full px-4 md:px-8 pb-12">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-3">
                  选择适合您的<span className="text-red-600">套餐</span>
                </h2>
                <p className="text-sm text-gray-400 text-center mb-8">选择最适合您的会员方案，解锁更多权益</p>

                {loading ? (
                  <div className="text-center py-16">
                    <div className="w-10 h-10 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-500">加载中...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                    {vipPlans.map((plan, idx) => {
                      const isSelected = selectedPlan === plan.id;
                      const isRecommended = idx === 1; // 中间位置高亮
                      
                      return (
                        <div
                          key={plan.id}
                          onClick={() => setSelectedPlan(plan.id)}
                          className={`relative cursor-pointer rounded-2xl transition-all duration-300 group ${
                            isSelected
                              ? "bg-gradient-to-b from-red-50 to-white border-2 border-red-500 shadow-xl shadow-red-500/10 scale-[1.02] md:scale-[1.05] z-10"
                              : "bg-white border-2 border-gray-200 hover:border-red-300 hover:shadow-lg"
                          }`}
                        >
                          {/* 推荐标签 */}
                          {plan.badge && (
                            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-20">
                              <span className={`px-4 py-1 rounded-full text-xs font-bold shadow-lg whitespace-nowrap ${
                                isSelected
                                  ? "bg-gradient-to-r from-red-600 to-rose-600 text-white"
                                  : "bg-red-500 text-white"
                              }`}>
                                {plan.badge}
                              </span>
                            </div>
                          )}

                          <div className="p-5 md:p-6">
                            {/* 套餐名 */}
                            <h3 className={`text-base font-bold mb-4 text-center ${
                              isSelected ? "text-red-700" : "text-gray-800"
                            }`}>
                              {plan.name}
                            </h3>

                            {/* 价格 */}
                            <div className="text-center mb-5">
                              <div className="flex items-baseline justify-center gap-1">
                                <span className="text-sm text-gray-400">¥</span>
                                <span className={`text-4xl font-extrabold ${
                                  isSelected ? "text-red-600" : "text-gray-800"
                                }`}>
                                  {plan.price}
                                </span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">/{plan.duration}</p>
                              {plan.originalPrice > plan.price && (
                                <p className="text-xs text-gray-400 mt-1.5">
                                  <span className="line-through">¥{plan.originalPrice}</span>
                                  <span className="ml-1.5 px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">
                                    省¥{plan.originalPrice - plan.price}
                                  </span>
                                </p>
                              )}
                            </div>

                            {/* 选中标记 */}
                            {isSelected && (
                              <div className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center rounded-full bg-red-500 shadow-md">
                                <i className="ri-check-line text-white text-xs" />
                              </div>
                            )}

                            {/* 特性列表 */}
                            <ul className="space-y-2.5 mb-5">
                              {plan.features.map((f, fi) => (
                                <li key={fi} className="flex items-start gap-2.5 text-sm">
                                  <i className={`mt-0.5 shrink-0 text-base ${
                                    isSelected
                                      ? "ri-checkbox-circle-fill text-red-500"
                                      : "ri-check-line text-gray-400"
                                  }`} />
                                  <span className={isSelected ? "text-gray-700" : "text-gray-500"}>
                                    {f}
                                  </span>
                                </li>
                              ))}
                            </ul>

                            {/* 套餐按钮 */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlan(plan.id);
                              }}
                              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                                isSelected
                                  ? "bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-lg shadow-red-500/25 hover:shadow-red-500/40"
                                  : "bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-200 hover:border-red-200"
                              }`}
                            >
                              {isSelected ? "✓ 已选择" : "选择此套餐"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── 支付方式 ── */}
                {!loading && (
                  <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-5 md:p-6 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">选择支付方式</h3>
                    <div className="flex gap-3 flex-wrap">
                      {showQrcode && (
                        <button
                          onClick={() => setPayMethod("qrcode")}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                            payMethod === "qrcode"
                              ? "border-red-500 bg-red-50 text-red-700 shadow-sm"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          <i className="ri-qr-code-line text-lg" />
                          收款码支付
                        </button>
                      )}
                      {showWechat && (
                        <button
                          onClick={() => setPayMethod("wechat")}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                            payMethod === "wechat"
                              ? "border-green-500 bg-green-50 text-green-700 shadow-sm"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          <i className="ri-wechat-pay-fill text-lg" />
                          微信支付
                        </button>
                      )}
                      {showAlipay && (
                        <button
                          onClick={() => setPayMethod("alipay")}
                          className={`flex items-center gap-2 px-5 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                            payMethod === "alipay"
                              ? "border-red-400 bg-red-50 text-red-700 shadow-sm"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                        >
                          <i className="ri-alipay-fill text-lg" />
                          支付宝
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── CTA 按钮 ── */}
                {!loading && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={handlePay}
                      className="inline-flex items-center gap-3 px-12 py-4.5 bg-gradient-to-r from-red-600 via-red-600 to-rose-600 text-white font-bold rounded-2xl hover:from-red-700 hover:to-rose-700 transition-all text-base shadow-xl shadow-red-500/30 hover:shadow-red-500/50 hover:-translate-y-0.5"
                    >
                      <i className="ri-vip-crown-line text-xl" />
                      <span>
                        立即开通 {currentPlan?.name} — ¥{currentPlan?.price}
                        {currentPlan && currentPlan.originalPrice > currentPlan.price && (
                          <span className="ml-2 text-white/70 text-xs line-through">¥{currentPlan.originalPrice}</span>
                        )}
                      </span>
                    </button>
                    <p className="text-xs text-gray-400 mt-3">
                      开通即享全部VIP权益，随时可取消
                    </p>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>

      {enableVip && <RedFooter />}

      {/* ── 支付弹窗 ── */}
      {enableVip && showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-800 text-lg">扫码支付</h3>
              <button
                onClick={() => setShowPayModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <div className="text-center">
              {/* 支付宝收款码 */}
              {payMethod === "alipay" && showAlipay && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用支付宝扫码付款</p>
                  <img
                    src={paymentConfig?.alipay_qrcode}
                    alt="支付宝收款码"
                    className="w-52 h-52 mx-auto rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {/* 微信收款码 */}
              {payMethod === "wechat" && showWechat && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">请使用微信扫码付款</p>
                  <img
                    src={paymentConfig?.wechat_qrcode}
                    alt="微信收款码"
                    className="w-52 h-52 mx-auto rounded-xl border border-gray-200"
                  />
                </div>
              )}

              {/* 双码展示 */}
              {payMethod === "qrcode" && (
                <div className="flex flex-col items-center gap-4">
                  {showAlipay && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">支付宝扫码付款</p>
                      <img
                        src={paymentConfig?.alipay_qrcode}
                        alt="支付宝收款码"
                        className="w-40 h-40 mx-auto rounded-xl border border-gray-200"
                      />
                    </div>
                  )}
                  {showWechat && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">微信扫码付款</p>
                      <img
                        src={paymentConfig?.wechat_qrcode}
                        alt="微信收款码"
                        className="w-40 h-40 mx-auto rounded-xl border border-gray-200"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* 无收款码占位 */}
              {!showAlipay && !showWechat && (
                <>
                  <div className="w-44 h-44 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center">
                    <i className="ri-qr-code-line text-5xl text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-600 mb-1">支付功能暂未启用</p>
                </>
              )}

              <p className="text-2xl font-bold text-red-600 mt-4 mb-1">¥{currentPlan?.price}</p>
              <p className="text-xs text-gray-400">{currentPlan?.name} · {currentPlan?.duration}</p>
            </div>

            <div className="mt-5 p-3.5 bg-red-50 rounded-xl text-xs text-red-700 text-center font-medium">
              <i className="ri-information-line mr-1" />
              支付成功后将自动开通VIP权益
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
